import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Tag, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 30000;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [offers, setOffers] = useState({ offers: [], unread_count: 0 });
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const wrapRef = useRef(null);

  const active = !!user && user !== false;
  const isParticular = active && user.role === "particular";

  const load = async () => {
    if (!active) return;
    try {
      const { data } = await api.get("/me/notifications?limit=20");
      setNotifs(data || { items: [], unread: 0 });
    } catch { /* noop */ }
    if (isParticular) {
      try {
        const { data } = await api.get("/me/pole-offers?limit=10");
        setOffers(data || { offers: [], unread_count: 0 });
      } catch { /* noop */ }
    }
  };

  useEffect(() => {
    if (!active) return;
    load();
    const iv = setInterval(load, POLL_MS);
    const refresh = () => load();
    window.addEventListener("focus", refresh);
    return () => { clearInterval(iv); window.removeEventListener("focus", refresh); };
    // eslint-disable-next-line
  }, [active]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const unreadTotal = (notifs.unread || 0) + (isParticular ? (offers.unread_count || 0) : 0);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unreadTotal > 0) {
      // Marque les offres vues
      if (isParticular && offers.unread_count > 0) {
        try { await api.post("/me/notifications/mark-read"); setOffers((d) => ({ ...d, unread_count: 0 })); } catch { /* noop */ }
      }
      // Marque chaque notification générale lue
      const unreadIds = (notifs.items || []).filter((n) => !n.read).map((n) => n.id);
      if (unreadIds.length) {
        Promise.allSettled(unreadIds.map((id) => api.post(`/me/notifications/${id}/read`)))
          .then(() => setNotifs((d) => ({ ...d, unread: 0, items: (d.items || []).map((n) => ({ ...n, read: true })) })));
      }
    }
  };

  const openNotif = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  if (!active) return null;
  const badge = unreadTotal > 0;

  return (
    <div className="relative" ref={wrapRef} data-testid="notif-bell-wrapper">
      <button
        onClick={toggle}
        data-testid="notif-bell-btn"
        aria-label={`Notifications ${badge ? `(${unreadTotal} non lues)` : ""}`}
        className={`relative p-2 rounded-lg hover:bg-slate-100 transition-colors ${open ? "bg-slate-100" : ""}`}
      >
        <Bell size={18} className={badge ? "text-[#E05638]" : "text-slate-700"} />
        {badge && (
          <span data-testid="notif-bell-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-rose-500 text-white text-[10px] font-extrabold border-2 border-white">
            {unreadTotal > 9 ? "9+" : unreadTotal}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[92vw] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden"
             data-testid="notif-bell-dropdown">
          <div className="max-h-[28rem] overflow-y-auto">
            {/* Activité (réponses aux commentaires, etc.) */}
            <div className="p-3 border-b border-slate-100 flex items-center justify-between">
              <div className="font-heading font-bold text-slate-900">Mon activité</div>
            </div>
            {(notifs.items || []).length === 0 ? (
              <div className="px-4 py-5 text-center text-sm text-slate-500" data-testid="notif-activity-empty">
                Aucune notification pour l'instant.
              </div>
            ) : (
              (notifs.items || []).map((n) => (
                <button
                  key={n.id}
                  onClick={() => openNotif(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left flex items-start gap-3 p-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${!n.read ? "bg-orange-50/40" : ""}`}
                >
                  <div className="w-9 h-9 rounded-full bg-[#E05638]/10 text-[#E05638] grid place-items-center shrink-0">
                    <MessageSquare size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{n.title}</div>
                    {n.message && <div className="text-xs text-slate-600 line-clamp-2">{n.message}</div>}
                    <div className="text-[11px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>
                  </div>
                </button>
              ))
            )}

            {/* Nouvelles offres (bénéficiaires uniquement) */}
            {isParticular && (
              <>
                <div className="p-3 border-b border-t border-slate-100 flex items-center justify-between">
                  <div className="font-heading font-bold text-slate-900">Nouvelles offres</div>
                  <Link to="/bons-plans" onClick={() => setOpen(false)} className="text-xs text-[#E05638] font-semibold">Voir tout</Link>
                </div>
                {offers.offers.length === 0 ? (
                  <div className="px-4 py-5 text-center text-sm text-slate-500">Aucune nouvelle offre pour l'instant.</div>
                ) : (
                  offers.offers.map((o) => {
                    const cover = (o.images && o.images[0]) || o.image;
                    return (
                      <Link key={o.id} to={`/offre/${o.id}`} onClick={() => setOpen(false)}
                        data-testid={`notif-offer-${o.id}`}
                        className="flex items-start gap-3 p-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
                        <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden shrink-0 grid place-items-center text-slate-300">
                          {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <Tag size={18} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="px-1.5 py-0.5 rounded-md text-[10px] font-extrabold bg-rose-500 text-white">{o.discount}</span>
                            {o.coverage === "department" && <span className="text-[10px] font-bold text-amber-700">Tout le 28</span>}
                          </div>
                          <div className="mt-0.5 text-sm font-semibold text-slate-900 line-clamp-2">{o.title}</div>
                          <div className="text-xs text-slate-500">{o.business_name}{o.business_name && o.city ? " · " : ""}{o.city}</div>
                        </div>
                      </Link>
                    );
                  })
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
